export const NAV_LINKS = [
  { id: "hero", label: "Home" },
  { id: "chaos", label: "The Chaos Tax" },
  { id: "vision", label: "Vision" },
  { id: "services", label: "Services" },
  { id: "contact", label: "Contact" },
];

export const HERO = {
  eyebrow: "AI-NATIVE SERVICES",
  headlineLines: ["AI IS READY.", "YOUR BUSINESS", "IS NOT."],
  sub: "You're plugging expensive tools into messy workflows. It's not working. That's not an AI problem — it's a foundation problem.",
  body: "We build clean data structures and streamlined workflows AI actually understands, so you can finally enjoy your morning coffee in peace.",
  cta: "Stop Automating Chaos — Let's Chat",
};

export const CHAOS = {
  label: "THE PROBLEM",
  title: "The Chaos Tax",
  intro: "Plugging AI into messy data is costing you daily:",
  items: [
    {
      no: "01",
      title: "Bad Decisions",
      body: "AI reads raw structure, not pretty layouts. Feed it messy data, and it confidently hallucinates.",
    },
    {
      no: "02",
      title: "Wasted Budget",
      body: "Buying expensive AI tools for broken workflows is just burning money. You're paying a premium to automate chaos.",
    },
    {
      no: "03",
      title: "Lost Sanity",
      body: "You didn't start a business to babysit spreadsheets.",
    },
  ],
};

export const VISION = {
  label: "VISION & MISSION",
  vision: {
    tag: "VISION",
    text: "To free ambitious builders from chaos, turning structure into their ultimate business superpower.",
  },
  mission: {
    tag: "MISSION",
    text: "We fix the foundations. Clean data, aligned workflows, and AI-ready systems — so your business can grow without burning you out.",
  },
};

export const BELIEF = {
  label: "OUR BELIEF",
  title: "A modern business must speak two languages.",
  chapters: [
    {
      no: "01",
      heading: "Readable by machines",
      body: "Your data, workflows, and websites shouldn't just make sense to your team. They must be instantly readable by the machines built to assist them.",
    },
    {
      no: "02",
      heading: "Clarity or failure",
      body: "AI runs on clarity, and without it, technology fails. We bring structure for agents as much as humans.",
    },
    {
      no: "03",
      heading: "Structure, built for you",
      body: "AI loves structure; we build it for you. So you can enjoy slow mornings, coffee in peace, and absolute peace of mind.",
    },
  ],
};

export const WHY = {
  label: "WHY INTELPRENEUR",
  title: "We fix foundations, not dashboards.",
  paragraphs: [
    "This wasn't built in some cozy corporate office, and it definitely didn't happen overnight. IntelPreneur came from a leap of faith — leaving behind a safe career, stepping into the unknown, and learning through trial, error, and failure.",
    "That journey revealed one simple truth: you can't hustle your way out of a messy business. We've lived the frantic schedules. We've tasted the burnout. And we learned that if you want a business that gives you your life back, you need a quiet, solid foundation first.",
    "That is why we take the messy, boring work off your plate — fixing your data, automating your workflows, and giving you back time for creativity and growth. We do it because we know exactly what it's like to drown in the alternative.",
  ],
  pillars: [
    "We fix foundations, not dashboards.",
    "We get your team looking at one version of the truth.",
    "We make AI practical and useful, not scary.",
    "We give you your peace of mind back.",
  ],
  kicker: "Hustling burns you out. Clarity scales you up. Let's fix the foundation.",
};

export const SERVICES = {
  label: "HOW WE HELP",
  title: "Services",
  items: [
    {
      no: "01",
      title: "AI Ready Foundations",
      body: "Everyone is building AI agents. No one is building the clean data they actually need to read. We audit your messy workflows, standardize your metrics, and turn chaotic data structures into clean, machine-readable assets.",
      result: "Humans and AI finally speak the same language.",
    },
    {
      no: "02",
      title: "AI Ready Apps",
      body: "Most apps, whether web or mobile, look fine to humans but are chaos to machines. We optimize your applications with agent-friendly protocols, structured APIs, and clean data flows, so AI agents can read, recommend, and integrate seamlessly.",
      result: "Your business isn't just visible — it's usable in the AI economy.",
    },
    {
      no: "03",
      title: "Custom Knowledge Assistants",
      body: "You don't need a generic chatbot that hallucinates and embarrasses your brand. Because we build on top of your clean data foundations, we can deploy secure, custom assistants directly to your website, Slack, or WhatsApp.",
      result: "A brilliant 24/7 assistant you can actually trust.",
    },
    {
      no: "04",
      title: "Privacy & Security Audits",
      body: "Most modern websites are \u201Cvibe-coded\u201D with major back-end leaks. We scan your public-facing assets to find security gaps and ensure aggressive AI scrapers aren't quietly stealing your proprietary business data.",
      result: "Total control over your digital assets.",
    },
    {
      no: "05",
      title: "On-Demand Data & AI Workshops",
      body: "Your team doesn't need to learn Python, and they don't need another generic tool tutorial. We design customized, hands-on sessions built around your workflows — teaching how machines think, how to manage data strategically, and how to decide when to trust AI and when to trust human judgment.",
      result: "No fear. Just calm, creative oversight.",
    },
  ],
};

export const SCANNER = {
  label: "AI-READINESS SCAN",
  title: "Is your website invisible to AI?",
  body: "Drop your URL. We'll scan your public setup, check your AI-readiness, and email you a dead-simple, honest score.",
  placeholder: "https://your-website.com",
  cta: "Check My Score",
  note: "// Scanner in private beta — you'll be added to the queue.",
};

export const CONTACT = {
  label: "CONTACT",
  title: "Clarity starts with a conversation.",
  body: "Contact IntelPreneur. Tell us where the mess is — we'll show you the foundation underneath it.",
};

export const LETTER = {
  label: "A LETTER TO THE INTELPRENEUR",
  greeting: "Dear IntelPreneur,",
  paragraphs: [
    "You left your 9-to-5 for a dream. You wanted to build something real, make an impact, and finally taste the luxury of slow mornings and drinking coffee in peace. Instead, you built yourself another job. Only this time, there is no clock-out button.",
    "When you calculate the endless hours you and your team are putting in against your actual profit, you've basically traded a 9-to-5 job for a 24/7 firefighting gig — just for a fancy title. And now, with AI everywhere, you thought you found the ultimate shortcut. But you fell into the same trap. AI plugged into disorder only adds noise.",
    "I hate to break it to you, but you cannot build an empire on top of a mess. If your workflows are chaotic and your data is unstructured, things will eventually fall apart. It doesn't matter how many brilliant employees you hire or how many expensive AI tools you deploy. You are just asking your team to build on quicksand.",
    "And that \u201Chustle culture\u201D they sold you? It's a scam. The promise of \u201Cwork 18 hours a day and become a millionaire in a year\u201D is just burning you out and stealing the beautiful, quiet moments you should be spending with the people you love.",
    "Think about our ancestors. The greatest ideas and empires in human history weren't born in a panic. They were born from moments of quiet and boredom. Boredom fuels the creativity that frantic hustling kills.",
    "So take a deep breath. Sit back. You will get where you want to go. Whether you move at speed 10 or speed 100, you will make it. Let us handle the boring, messy, unsexy back-end work. Go enjoy your coffee. We've got this.",
  ],
  signOff: "With scientific intent and human empathy,",
  signature: "Rafka Abou Salem, Founder",
  portrait: "https://images.pexels.com/photos/29433729/pexels-photo-29433729.png",
};

export const STATS = [
  { value: "1", unit: "SOURCE OF TRUTH", label: "One aligned dataset for humans + agents" },
  { value: "24/7", unit: "ASSISTANTS", label: "Custom knowledge assistants you can trust" },
  { value: "0", unit: "HALLUCINATIONS", label: "Clean structure, honest outputs" },
  { value: "100%", unit: "PEACE OF MIND", label: "We take the messy work off your plate" },
];

export const IMAGES = {
  serverRack: "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg",
  fibers: "https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg",
};

export const SPLINE_SCENE = "https://prod.spline.design/1NiHY-rZ0cfuXiL0/scene.splinecode";
